/**
 * One Reports ▸ Projects page. Same suite report, saved views and drill drawer as the other native reports.
 * A stale or missing `report_project_facts` read is the word "unknown" and the source name. It is not a row of zeros.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import * as React from 'react'
import { Frame, PaneHeader } from '../../../components/frame'
import type { Drill, ReportResult } from '../../../lib/api/generated'
import {
  CLEARED_FILTERS,
  CLOSED_DRILL,
  catalogueQuery,
  drillSearch,
  type ReportsSearch,
  searchFromFilters,
  suiteReportQuery,
  toApiFilters,
} from '../api'
import { ChartCard, chartHidden, KpiStrip, metricVisible, ReportTable } from '../charts'
import { DrillDrawer } from '../drill-drawer'
import { Breadcrumb } from '../report'
import { useCanSee } from '../roles'
import { dateLabel, QueryError } from '../shared'
import { SaveViewMenu } from '../views-menu'
import { ProjectFilters } from './filters'
import { PROJECT_REPORTS, type ProjectReportKey } from './keys'

function UnknownFacts({ result }: { result: ReportResult }) {
  const source = (result.sources ?? []).find((item) => item.name === 'report_project_facts') ?? result.sources?.[0]
  const name = source?.name ?? 'report_project_facts'
  const label = source?.note ? `unknown: ${name}, ${source.note}` : `unknown: ${name}`
  return (
    <div role="status" aria-label={label} data-facts="stale" className="rounded-lg border border-border bg-card px-4 py-6">
      <p className="text-lg font-semibold text-foreground">unknown</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {name}
        {source?.note ? ` · ${source.note}` : ''}
      </p>
    </div>
  )
}

function ProjectBody({ result, onDrill, updating }: { result: ReportResult; onDrill: (drill: Drill) => void; updating?: boolean }) {
  const canSee = useCanSee()
  if (result.state === 'unavailable') return <UnknownFacts result={result} />
  const kpis = (result.kpis ?? []).filter((metric) => metricVisible(metric, canSee))
  const charts = (result.charts ?? []).filter((chart) => !chartHidden(chart, canSee))
  const tables = result.tables ?? []
  return (
    <div className={updating ? 'space-y-5 opacity-60' : 'space-y-5'} data-updating={updating || undefined}>
      {kpis.length ? (
        <section aria-labelledby="project-kpis">
          <h2 id="project-kpis" className="sr-only">
            Headline figures
          </h2>
          <KpiStrip metrics={kpis} onDrill={onDrill} canSee={canSee} />
        </section>
      ) : null}
      {charts.length ? (
        <section aria-labelledby="project-charts">
          <h2 id="project-charts" className="sr-only">
            Charts
          </h2>
          <div className="grid grid-cols-1 gap-4 pane:grid-cols-2">
            {charts.map((chart, index) => (
              <div key={chart.key} className={index === 0 ? 'min-w-0 pane:col-span-2' : 'min-w-0'}>
                <ChartCard chart={chart} onDrill={onDrill} drillReport={result.key} wide={index === 0} canSee={canSee} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {tables.length ? (
        <section aria-labelledby="project-tables" className="space-y-4">
          <h2 id="project-tables" className="sr-only">
            Tables
          </h2>
          {tables.map((table) => (
            <ReportTable key={table.key} table={table} onDrill={onDrill} canSee={canSee} />
          ))}
        </section>
      ) : null}
    </div>
  )
}

function ReportNav({ current, search }: { current: ProjectReportKey; search: ReportsSearch }) {
  const { drill: _drill, dp: _dp, dr: _dr, ...kept } = search
  return (
    <nav aria-label="Project reports" className="flex flex-wrap gap-1">
      {PROJECT_REPORTS.map(([key, label]) => (
        <Link
          key={key}
          to="/reports/$reportId"
          params={{ reportId: key }}
          search={kept}
          aria-current={key === current ? 'page' : undefined}
          className={
            key === current
              ? 'rounded-md bg-primary-soft px-2.5 py-1 text-xs font-medium text-primary'
              : 'rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground'
          }
        >
          {label}
        </Link>
      ))}
    </nav>
  )
}

export function ProjectsReportScreen({
  reportKey,
  search,
  onSearch,
  onCloseDrill,
}: {
  reportKey: ProjectReportKey
  search: ReportsSearch
  onSearch: (patch: Partial<ReportsSearch>) => void
  onCloseDrill?: () => void
}) {
  const canSee = useCanSee()
  const catalogue = useQuery(catalogueQuery(search.brand))
  const filters = React.useMemo(() => toApiFilters(search), [search])
  const report = useQuery({
    ...suiteReportQuery(reportKey, filters),
    placeholderData: (prev, prevQuery) => (prev && prevQuery?.queryKey[2] === reportKey ? prev : undefined),
  })
  const title = report.data?.title ?? PROJECT_REPORTS.find(([key]) => key === reportKey)?.[1] ?? reportKey
  const window = report.data?.window
  const subtitle = window ? `${dateLabel(window.from)} to ${dateLabel(window.to)}` : 'Projects'
  const onDrill = React.useCallback((drill: Drill) => onSearch(drillSearch(drill, reportKey)), [onSearch, reportKey])
  const onApply = React.useCallback(
    (view: { filters: Parameters<typeof searchFromFilters>[0] }) =>
      onSearch({ ...CLEARED_FILTERS, ...CLOSED_DRILL, ...searchFromFilters(view.filters) }),
    [onSearch],
  )

  return (
    <Frame
      active="reports"
      railless
      main={
        <div className="reports-root flex h-full flex-col overflow-y-auto" data-scroll-top>
          <PaneHeader
            title={<Breadcrumb title={title} />}
            subtitle={subtitle}
            actions={
              <span className="flex items-center gap-3">
                <ReportNav current={reportKey} search={search} />
                <SaveViewMenu report={reportKey} filters={filters} onApply={onApply} />
              </span>
            }
          />
          <ProjectFilters search={search} onChange={onSearch} options={catalogue.data?.filter_options} />
          <main className="w-full max-w-7xl space-y-4 px-4 py-4 sm:px-5" aria-busy={report.isFetching || undefined}>
            {report.isPending ? (
              <div className="grid grid-cols-2 gap-3 pane:grid-cols-4" role="status" aria-label="Loading report">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            ) : report.isError || !report.data ? (
              <QueryError error={report.error} what={title} onRetry={() => void report.refetch()} />
            ) : (
              <ProjectBody result={report.data} onDrill={onDrill} updating={report.isPlaceholderData} />
            )}
          </main>
          {search.drill ? (
            <DrillDrawer
              report={search.dr ?? reportKey}
              metric={search.drill}
              params={search.dp ?? []}
              filters={filters}
              canSee={canSee}
              onClose={onCloseDrill ?? (() => onSearch(CLOSED_DRILL))}
            />
          ) : null}
        </div>
      }
    />
  )
}
